import { getExerciseServicePromiseClient } from '../utils/grpcClient';
import * as pb from '../generated/fitness-service/v1/exercise_pb';

// Exercise interfaces
export interface Exercise {
    id: string;
    name: string;
    description: string;
    category: string;
    muscle_group: string;
    equipment: string;
    instructions: string;
    image_url: string;
    created_by: string;
    is_public: boolean;
    created_at: string;
    updated_at: string;
}

export interface GetExercisesParams {
    page?: number;
    limit?: number;
    category?: string;
    muscle_group?: string;
    equipment?: string;
    public_only?: boolean;
    search?: string;
}

export interface ExercisesResponse {
    exercises: Exercise[];
    total_count: number;
    page: number;
    limit: number;
}

export interface CreateExerciseParams {
    name: string;
    description: string;
    category: string;
    muscle_group: string;
    equipment: string;
    instructions: string;
    image_url?: string;
    is_public: boolean;
}

export interface UpdateExerciseParams {
    name: string;
    description: string;
    category: string;
    muscle_group: string;
    equipment: string;
    instructions: string;
    image_url?: string;
    is_public: boolean;
}

class ExerciseService {
    private client = getExerciseServicePromiseClient();

    // Helper method to get authentication metadata
    private getAuthMetadata() {
        const token = localStorage.getItem('authToken');
        return token ? { 'authorization': `Bearer ${token}` } : {};
    }

    // Helper method to handle errors
    private handleError(error: any): Error {
        const message = error.message || 'An unknown error occurred';
        console.error('Exercise service error:', error);
        return new Error(message);
    }

    // Helper method to map protobuf Exercise to our interface
    private mapExercise(exercise: any): Exercise {
        return {
            id: exercise.getId(),
            name: exercise.getName(),
            description: exercise.getDescription(),
            category: exercise.getCategory(),
            muscle_group: exercise.getMuscleGroup(),
            equipment: exercise.getEquipment(),
            instructions: exercise.getInstructions(),
            image_url: exercise.getImageUrl(),
            created_by: exercise.getCreatedBy(),
            is_public: exercise.getIsPublic(),
            created_at: exercise.getCreatedAt()?.toDate?.()?.toISOString() || '',
            updated_at: exercise.getUpdatedAt()?.toDate?.()?.toISOString() || '',
        };
    }

    // Get exercises with filters
    async getExercises(params: GetExercisesParams = {}): Promise<ExercisesResponse> {
        const request = new pb.GetExercisesRequest();

        if (params.page !== undefined) request.setPage(params.page);
        if (params.limit !== undefined) request.setLimit(params.limit);
        if (params.category) request.setCategory(params.category);
        if (params.muscle_group) request.setMuscleGroup(params.muscle_group);
        if (params.equipment) request.setEquipment(params.equipment);
        if (params.public_only !== undefined) request.setPublicOnly(params.public_only);
        if (params.search) request.setSearch(params.search);

        try {
            const response = await this.client.getExercises(request, this.getAuthMetadata());

            const exercises: Exercise[] = response.getExercisesList().map((exercise: any) =>
                this.mapExercise(exercise)
            );

            return {
                exercises,
                total_count: response.getTotalCount(),
                page: response.getPage(),
                limit: response.getLimit(),
            };
        } catch (error) {
            console.error('Error fetching exercises:', error);
            throw this.handleError(error);
        }
    }

    // Get single exercise
    async getExercise(exerciseId: string): Promise<Exercise> {
        const request = new pb.GetExerciseRequest();
        request.setExerciseId(exerciseId);

        try {
            const response = await this.client.getExercise(request, this.getAuthMetadata());
            return this.mapExercise(response.getExercise()!);
        } catch (error) {
            console.error('Error fetching exercise:', error);
            throw this.handleError(error);
        }
    }

    // Create new exercise
    async createExercise(params: CreateExerciseParams): Promise<Exercise> {
        const request = new pb.CreateExerciseRequest();
        request.setName(params.name);
        request.setDescription(params.description);
        request.setCategory(params.category);
        request.setMuscleGroup(params.muscle_group);
        request.setEquipment(params.equipment);
        request.setInstructions(params.instructions);
        if (params.image_url) request.setImageUrl(params.image_url);
        request.setIsPublic(params.is_public);

        try {
            const response = await this.client.createExercise(request, this.getAuthMetadata());
            const exercise = response.getExercise();
            if (!exercise) {
                throw new Error('No exercise data returned from server');
            }
            return this.mapExercise(exercise);
        } catch (error) {
            console.error('Error creating exercise:', error);
            throw this.handleError(error);
        }
    }

    // Update existing exercise
    async updateExercise(exerciseId: string, params: UpdateExerciseParams): Promise<Exercise> {
        const request = new pb.UpdateExerciseRequest();
        request.setExerciseId(exerciseId);
        request.setName(params.name);
        request.setDescription(params.description);
        request.setCategory(params.category);
        request.setMuscleGroup(params.muscle_group);
        request.setEquipment(params.equipment);
        request.setInstructions(params.instructions);
        if (params.image_url) request.setImageUrl(params.image_url);
        request.setIsPublic(params.is_public);

        try {
            const response = await this.client.updateExercise(request, this.getAuthMetadata());
            const exercise = response.getExercise();
            if (!exercise) {
                throw new Error('No exercise data returned from server');
            }
            return this.mapExercise(exercise);
        } catch (error) {
            console.error('Error updating exercise:', error);
            throw this.handleError(error);
        }
    }

    // Delete exercise
    async deleteExercise(exerciseId: string): Promise<void> {
        const request = new pb.DeleteExerciseRequest();
        request.setExerciseId(exerciseId);

        try {
            await this.client.deleteExercise(request, this.getAuthMetadata());
        } catch (error) {
            console.error('Error deleting exercise:', error);
            throw this.handleError(error);
        }
    }
}

// Export singleton instance
export const exerciseService = new ExerciseService();
export default exerciseService;
